import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
import { ExternalLink, Mail, Package, DollarSign, Calendar, MapPin, User as UserIcon, Copy, Eye, Pencil, Save, Loader2 } from "lucide-react";
import { format } from "date-fns";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useEffect, useState } from "react";
import { AdminBoxEditDialog } from "./AdminBoxEditDialog";

interface UserData {
  id: string;
  email: string;
  full_name: string | null;
  phone: string;
  city: string | null;
  state: string | null;
  zip: string | null;
  address_line1: string | null;
  address_line2?: string | null;
  created_at: string;
  signup_source: string | null;
  use_case: string | null;
  billing_start_date: string | null;
  box_slots_purchased?: number | null;
  box_slots_used?: number | null;
  stripe_customer_id?: string | null;
  stripe_subscription_id?: string | null;
  total_boxes: number;
  total_items: number;
  monthly_revenue: number;
  months_stored: number | null;
  status: 'active' | 'inactive' | 'never_stored';
  stored_boxes: number;
}

interface Box {
  id: string;
  box_number: string;
  name: string;
  location_status: string;
  storage_start_date: string | null;
  stored_at: string | null;
  created_at: string;
}

interface UserDetailSheetProps {
  user: UserData | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export const UserDetailSheet = ({ user, open, onOpenChange }: UserDetailSheetProps) => {
  const { toast } = useToast();
  const [boxes, setBoxes] = useState<Box[]>([]);
  const [loadingBoxes, setLoadingBoxes] = useState(false);
  const [editingBox, setEditingBox] = useState<Box | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  
  // Profile editing state
  const [stripeCustomerId, setStripeCustomerId] = useState<string>("");
  const [stripeSubscriptionId, setStripeSubscriptionId] = useState<string>("");
  const [boxSlotsPurchased, setBoxSlotsPurchased] = useState<string>("");
  const [billingStartDate, setBillingStartDate] = useState<string>("");
  const [savingProfile, setSavingProfile] = useState(false);
  const [hasProfileChanges, setHasProfileChanges] = useState(false);

  useEffect(() => {
    if (user && open) {
      fetchUserBoxes();
      // Initialize editable fields from user data
      setStripeCustomerId(user.stripe_customer_id || "");
      setStripeSubscriptionId(user.stripe_subscription_id || "");
      setBoxSlotsPurchased(user.box_slots_purchased?.toString() || "");
      setBillingStartDate(user.billing_start_date || "");
      setHasProfileChanges(false);
    }
  }, [user, open]);

  // Track changes to editable fields
  useEffect(() => {
    if (user) {
      const customerChanged = stripeCustomerId !== (user.stripe_customer_id || "");
      const subscriptionChanged = stripeSubscriptionId !== (user.stripe_subscription_id || "");
      const slotsChanged = boxSlotsPurchased !== (user.box_slots_purchased?.toString() || "");
      const billingChanged = billingStartDate !== (user.billing_start_date || "");
      setHasProfileChanges(customerChanged || subscriptionChanged || slotsChanged || billingChanged);
    }
  }, [stripeCustomerId, stripeSubscriptionId, boxSlotsPurchased, billingStartDate, user]);

  const fetchUserBoxes = async () => {
    if (!user) return;
    
    setLoadingBoxes(true);
    try {
      const { data, error } = await supabase
        .from('boxes')
        .select('id, box_number, name, location_status, storage_start_date, stored_at, created_at')
        .eq('user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setBoxes(data || []);
    } catch (error) {
      console.error('Error fetching boxes:', error);
      toast({
        title: "Error loading boxes",
        description: "Failed to load user's boxes",
        variant: "destructive",
      });
    } finally {
      setLoadingBoxes(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied!",
      description: `${label} copied to clipboard`,
    });
  };

  const handleSaveProfile = async () => {
    if (!user) return;

    setSavingProfile(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('admin-update-profile', {
        body: {
          profileId: user.id,
          updates: {
            stripe_customer_id: stripeCustomerId || null,
            stripe_subscription_id: stripeSubscriptionId || null,
            box_slots_purchased: boxSlotsPurchased ? parseInt(boxSlotsPurchased, 10) : null,
            billing_start_date: billingStartDate || null,
          }
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      toast({
        title: "Saved",
        description: "Profile updated successfully",
      });
      
      // Update local state to reflect saved values
      setHasProfileChanges(false);
    } catch (error: any) {
      console.error('Error saving profile:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save profile",
        variant: "destructive",
      });
    } finally {
      setSavingProfile(false);
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast({
          title: "Error",
          description: "You must be logged in",
          variant: "destructive",
        });
        return;
      }

      const { data, error } = await supabase.functions.invoke('impersonate-user', {
        body: { 
          targetEmail: user.email,
          originUrl: window.location.origin 
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) throw error;

      // Store the admin recovery link and impersonation state BEFORE navigating
      sessionStorage.setItem('admin_recovery_link', data.adminRecoveryLink);
      sessionStorage.setItem('impersonation_active', 'true');
      sessionStorage.setItem('impersonation_target', user.email);

      toast({
        title: "Impersonation starting",
        description: `Switching to ${user.email}'s session...`,
      });

      // Navigate to the magic link to switch sessions
      window.location.href = data.actionLink;
    } catch (error: any) {
      console.error('Error starting impersonation:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to start impersonation",
        variant: "destructive",
      });
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "active":
        return <Badge className="bg-green-500">Active</Badge>;
      case "inactive":
        return <Badge variant="secondary">Inactive</Badge>;
      case "never_stored":
        return <Badge variant="outline">Never Stored</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const getLocationBadge = (status: string) => {
    switch (status) {
      case "stored":
        return <Badge>Stored</Badge>;
      case "in_transit":
        return <Badge variant="secondary">In Transit</Badge>;
      case "home":
        return <Badge variant="outline">At Home</Badge>;
      case "pickup_scheduled":
        return <Badge variant="secondary">Pickup Scheduled</Badge>;
      case "returning":
        return <Badge variant="secondary">Returning</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  if (!user) return null;

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
    }).format(amount);
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <UserIcon className="h-5 w-5" />
            {user.full_name || "User Details"}
          </SheetTitle>
          <SheetDescription>{user.email}</SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-6">
          {/* Status and Quick Actions */}
          <div className="flex items-center justify-between">
            {getStatusBadge(user.status)}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={() => copyToClipboard(user.email, "Email")}
              >
                <Copy className="h-4 w-4 mr-1" />
                Copy Email
              </Button>
              <Button size="sm" variant="outline" onClick={handleImpersonate}>
                <Eye className="h-4 w-4 mr-1" />
                Impersonate
              </Button>
              {stripeCustomerId && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeCustomerId}`, '_blank')}
                >
                  <ExternalLink className="h-4 w-4 mr-1" />
                  Stripe
                </Button>
              )}
            </div>
          </div>

          <Separator />

          {/* Profile Information */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <UserIcon className="h-4 w-4" />
              Profile Information
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Phone:</span>
                <p className="font-medium">{user.phone}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Signup Date:</span>
                <p className="font-medium">{format(new Date(user.created_at), "MMM d, yyyy")}</p>
              </div>
              {user.use_case && (
                <div className="col-span-2">
                  <span className="text-muted-foreground">Use Case:</span>
                  <p className="font-medium">{user.use_case}</p>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Address */}
          {user.address_line1 && (
            <>
              <div>
                <h3 className="font-semibold mb-3 flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address
                </h3>
                <div className="text-sm space-y-1">
                  <p>{user.address_line1}</p>
                  {user.address_line2 && <p>{user.address_line2}</p>}
                  <p>
                    {user.city && `${user.city}, `}
                    {user.state} {user.zip}
                  </p>
                </div>
              </div>
              <Separator />
            </>
          )}

          {/* Storage Metrics */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Storage Metrics
            </h3>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Total Boxes:</span>
                <p className="font-medium text-lg">{user.total_boxes}</p>
              </div>
              <div>
                <span className="text-muted-foreground">In Storage:</span>
                <p className="font-medium text-lg">{user.stored_boxes}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Total Items:</span>
                <p className="font-medium text-lg">{user.total_items}</p>
              </div>
              <div>
                <span className="text-muted-foreground">Box Slots:</span>
                <p className="font-medium text-lg">
                  {user.box_slots_used || 0} / {user.box_slots_purchased || 0}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Subscription Info */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <DollarSign className="h-4 w-4" />
              Subscription
            </h3>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">Monthly Revenue:</span>
                  <p className="font-medium text-lg">{formatCurrency(user.monthly_revenue)}</p>
                </div>
                {user.months_stored !== null && (
                  <div>
                    <span className="text-muted-foreground">Months Stored:</span>
                    <p className="font-medium text-lg">{user.months_stored}</p>
                  </div>
                )}
                {user.billing_start_date && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Billing Start:</span>
                    <p className="font-medium">{format(new Date(user.billing_start_date), "MMM d, yyyy")}</p>
                  </div>
                )}
                {user.signup_source && (
                  <div className="col-span-2">
                    <span className="text-muted-foreground">Signup Source:</span>
                    <Badge variant="outline">{user.signup_source}</Badge>
                  </div>
                )}
              </div>

              {/* Editable Profile Fields */}
              <div className="space-y-3 pt-2 border-t">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm text-muted-foreground block mb-1">
                      Box Slots Purchased
                    </label>
                    <Input
                      type="number"
                      min="0"
                      value={boxSlotsPurchased}
                      onChange={(e) => setBoxSlotsPurchased(e.target.value)}
                      placeholder="0"
                      className="text-sm"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-muted-foreground block mb-1">
                      Billing Start Date
                    </label>
                    <Input
                      type="date"
                      value={billingStartDate}
                      onChange={(e) => setBillingStartDate(e.target.value)}
                      className="text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground block mb-1">
                    Stripe Customer ID
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={stripeCustomerId}
                      onChange={(e) => setStripeCustomerId(e.target.value)}
                      placeholder="cus_..."
                      className="font-mono text-sm"
                    />
                    {stripeCustomerId && (
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => window.open(`https://dashboard.stripe.com/customers/${stripeCustomerId}`, '_blank')}
                        title="View in Stripe"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                <div>
                  <label className="text-sm text-muted-foreground block mb-1">
                    Stripe Subscription ID
                  </label>
                  <div className="flex gap-2">
                    <Input
                      value={stripeSubscriptionId}
                      onChange={(e) => setStripeSubscriptionId(e.target.value)}
                      placeholder="sub_..."
                      className="font-mono text-sm"
                    />
                    {stripeSubscriptionId && (
                      <Button
                        size="icon"
                        variant="outline"
                        onClick={() => window.open(`https://dashboard.stripe.com/subscriptions/${stripeSubscriptionId}`, '_blank')}
                        title="View in Stripe"
                      >
                        <ExternalLink className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                </div>

                {hasProfileChanges && (
                  <Button
                    onClick={handleSaveProfile}
                    disabled={savingProfile}
                    className="w-full"
                  >
                    {savingProfile ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <Save className="h-4 w-4 mr-2" />
                        Save Changes
                      </>
                    )}
                  </Button>
                )}
              </div>
            </div>
          </div>

          <Separator />

          {/* Boxes List */}
          <div>
            <h3 className="font-semibold mb-3 flex items-center gap-2">
              <Package className="h-4 w-4" />
              Boxes ({boxes.length})
            </h3>
            {loadingBoxes ? (
              <p className="text-sm text-muted-foreground">Loading boxes...</p>
            ) : boxes.length === 0 ? (
              <p className="text-sm text-muted-foreground">No boxes yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {boxes.map((box) => (
                  <div
                    key={box.id}
                    className="flex items-center justify-between p-3 border rounded-lg hover:bg-muted/50"
                  >
                    <div className="flex-1">
                      <p className="font-medium text-sm">{box.name}</p>
                      <p className="text-xs text-muted-foreground">#{box.box_number}</p>
                      {box.storage_start_date && (
                        <p className="text-xs text-muted-foreground">
                          <Calendar className="h-3 w-3 inline mr-1" />
                          {format(new Date(box.storage_start_date), "MMM d, yyyy")}
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-2">
                      {getLocationBadge(box.location_status)}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => {
                          setEditingBox(box);
                          setEditDialogOpen(true);
                        }}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <AdminBoxEditDialog
          box={editingBox}
          open={editDialogOpen}
          onOpenChange={setEditDialogOpen}
          onSaved={fetchUserBoxes}
        />
      </SheetContent>
    </Sheet>
  );
};
